/**
 * 区域编码表（协会 GetArea 那 24 条）的取用装置。
 *
 * ★ 为什么区域**只能选、不许自由输入**：`区域ID` 是要推协会老系统的，
 *   填一个表外的值推送当场失败，而老系统**没有删除接口** —— 推错了就是一条改不掉的
 *   脏记录，且失败时界面上没有任何提示。所以取值只有一个来源：这张表。
 *   （出处：契约真本 `prefill-contract.ts` 的 `CITIZEN_AREAS_ROUTE` 注释。）
 *
 * ★ 拉不到表时**不许回落成一份写死的 24 条**（市民端红线：不做 mock 回落）。
 *   拉不到就是拉不到 —— 下拉禁用、说明白、给一个「重试」，
 *   并且**照样能提交**（区域本来就是选填）。
 */

import { useCallback, useEffect, useState } from 'react';
import { getAreas } from '../api/prefill';
import { errText } from '../api/client';
import type { AreaOption } from '@cdfjx/shared/dist/prefill-contract';

export interface AreasHandle {
  /** `null` ＝ 还没拉到（加载中或失败）。**空数组与 null 不是一回事** */
  items: AreaOption[] | null;
  loading: boolean;
  /** 拉失败的中文原因；成功或加载中为空串 */
  error: string;
  reload: () => void;
}

export function useAreas(enabled: boolean): AreasHandle {
  const [items, setItems] = useState<AreaOption[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  /** 手动重试用的计数器：变一次就重跑一次 effect */
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled || items !== null) return;
    let alive = true;
    setLoading(true);
    setError('');
    getAreas()
      .then((r) => {
        if (!alive) return;
        setItems(r.items);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (!alive) return;
        // 后端给的中文原因原样留着，不换成「加载失败」那种什么都没说的话
        setError(errText(e));
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [enabled, items, tick]);

  const reload = useCallback(() => setTick((n) => n + 1), []);

  return { items, loading, error, reload };
}
